import { OCCUPANCY_TOKEN, occupancyOf } from '../tabs/packageOptions/previousPackageVariants';

import {
  blankedErrorNote,
  blankedPriceNote,
  cellAddress,
  cellAt,
  findImportColumns,
  isUnpriced,
  numberOf,
  positiveNumberOf,
  priceOf,
  textOf,
  unusablePriceProblem,
  warnAt,
} from './sheetCells';
import type { ParsedDealOption, SheetContext } from './types';

const COLUMNS = {
  name: 'Deal Option Name',
  sell: 'Sell Price (AUD)',
  nights: 'Number of Nights',
  roomType: 'Room Type',
  net: 'Cost Price',
  extraNightNet: 'Nightly Cost Price',
  extraNightSell: 'Nightly Sell Price (AUD)',
  allocation: 'Allocation Per Day',
} as const;

type Column = keyof typeof COLUMNS;

const LEADING_OCCUPANCY = new RegExp(`^${OCCUPANCY_TOKEN}`, 'i');

export interface ParsedDealOptions {
  dealOptions: Array<ParsedDealOption>;
  skippedVariants: Array<string>;
}

const squash = (text: string) => text.replace(/\s+/g, '').toLowerCase();

// Blank and 0 are how the template leaves a stay nobody priced; any other unusable sell is the PCM's to fix.
function unusableSellMessage(sell: unknown): string {
  return `${unusablePriceProblem(sell, 'Sell Price (AUD)')} Add it on Package Options.`;
}

// Whitespace and case are ignored on both sides, so "5N Pool Villa" still matches a name typed "5N PoolVilla".
function textAfterPrefix(name: string, prefix: string): string | null {
  const wanted = squash(prefix);
  let matched = 0;
  let index = 0;
  while (matched < wanted.length) {
    if (index >= name.length) {
      return null;
    }
    const char = name[index].toLowerCase();
    index += 1;
    if (/\s/.test(char)) {
      continue;
    }
    if (char !== wanted[matched]) {
      return null;
    }
    matched += 1;
  }
  return name.slice(index).trim();
}

// A base package is named "{nights}N {Room Type}" plus an optional occupancy; anything after that is a variant
// (transfers, upgrades) that the PCM adds on Package Options afterwards.
export function parseDealOptions(context: SheetContext): ParsedDealOptions {
  const columns = findImportColumns(context, COLUMNS, 'dealOptions', 'deal options');
  if (!columns) {
    return { dealOptions: [], skippedVariants: [] };
  }

  const dealOptions: Array<ParsedDealOption> = [];
  const skippedVariants: Array<string> = [];
  const loadedRowByStay = new Map<string, number>();
  const roomTypeByKey = new Map<string, string>();

  const reject = (row: number, column: Column, message: string) =>
    context.issues.push({
      section: 'dealOptions',
      severity: 'error',
      cell: cellAddress(row, columns[column]),
      row: row + 1,
      message,
    });

  for (let row = 1; row < context.rows.length; row++) {
    const cell = (column: Column) => cellAt(context.rows, row, columns[column]);
    const typedRoomType = textOf(cell('roomType'));
    const sell = priceOf(cell('sell'));
    // The template fills Room Type in for every stay block, so a row nobody priced is template, not a mistake.
    if (sell === null) {
      if (!isUnpriced(cell('sell'))) {
        reject(row, 'sell', unusableSellMessage(cell('sell')));
      }
      continue;
    }
    if (!typedRoomType || typedRoomType === '0') {
      reject(row, 'roomType', 'the Room Type is empty.');
      continue;
    }
    const nights = positiveNumberOf(cell('nights'));
    if (nights === null || !Number.isInteger(nights)) {
      reject(row, 'nights', 'the Number of Nights is not a whole number.');
      continue;
    }

    const roomType = typedRoomType.replace(/\s+/g, ' ');
    const name = textOf(cell('name'));
    if (!name) {
      reject(row, 'name', 'the Deal Option Name is empty.');
      continue;
    }
    if (name.startsWith('#')) {
      const error = name.replace(/^#ERROR_/, '');
      reject(row, 'name', `the Deal Option Name shows ${error}. Fix the workbook and upload again.`);
      continue;
    }
    const rest = textAfterPrefix(name, `${nights}N ${roomType}`);
    if (rest === null) {
      reject(row, 'name', `the Deal Option Name doesn't start with "${nights}N ${roomType}".`);
      continue;
    }
    const occupancy = LEADING_OCCUPANCY.exec(rest)?.[0];
    const variant = rest
      .slice(occupancy?.length ?? 0)
      .trim()
      .replace(/\s+/g, ' ');
    if (variant) {
      skippedVariants.push(variant);
      continue;
    }

    const roomKey = squash(roomType);
    const stayKey = `${nights}::${roomKey}`;
    const loadedRow = loadedRowByStay.get(stayKey);
    if (loadedRow !== undefined) {
      reject(row, 'name', `${nights}N ${roomType} is already on row ${loadedRow + 1}.`);
      continue;
    }
    loadedRowByStay.set(stayKey, row);
    if (!roomTypeByKey.has(roomKey)) {
      roomTypeByKey.set(roomKey, roomType);
    }

    const where = (column: Column) => ({ column: COLUMNS[column], row: row + 1, tab: 'Package Options' });
    const warn = (column: Column, note: string | null) => warnAt(context, 'dealOptions', row, columns[column], note);
    const optionalPrice = (column: 'net' | 'extraNightSell' | 'extraNightNet') => {
      const price = priceOf(cell(column));
      warn(column, blankedPriceNote(cell(column), price, where(column)));
      return price;
    };
    const allocationCell = cell('allocation');
    const allocation = warn('allocation', blankedErrorNote(allocationCell, where('allocation')))
      ? ''
      : (numberOf(allocationCell)?.toString() ?? textOf(allocationCell) ?? '');
    dealOptions.push({
      row: row + 1,
      roomType: roomTypeByKey.get(roomKey) ?? roomType,
      nights,
      occupancy: occupancy ? occupancyOf(occupancy) : null,
      sell,
      net: optionalPrice('net'),
      extraNightSell: optionalPrice('extraNightSell'),
      extraNightNet: optionalPrice('extraNightNet'),
      allocation,
    });
  }

  return { dealOptions, skippedVariants };
}
